import { useCallback, useEffect, useState } from "react";
import "../incident.css";
import {
  ACTION_LABEL, ACTOR_LABEL, IChangeRequest, IIncidentHistory, INCIDENT_TYPES, IncidentType, REASON_MAX, DESCRIPTION_MAX,
  REPORTER_LABEL, REQUEST_STATUS_LABEL, STATUS_META, typeIcon, typeLabel,
} from "../models/IIncident";
import {
  adminCorrect, adminDispute, adminRemove, adminRestore, decideChangeRequest, getAdminIncident, getEvidence, incidentError,
} from "../services/incidentService";
import { StatusBadge, formatDateTime } from "./IncidentBadges";

interface Props {
  incidentId: string;
  restaurantName?: string;
  onClose: () => void;
  /** Kayıt değişti; listeler yenilensin */
  onChanged?: () => void;
}

type Action = "correct" | "dispute" | "restore" | "remove" | "approve" | "reject";

const ACTIONS: Record<Action, { label: string; icon: string; btn: string; hint: string }> = {
  correct: { label: "Düzelt", icon: "bi-pencil-square", btn: "inc-btn--dark", hint: "Tür ve/veya açıklama düzeltilir; durum değişmez." },
  dispute: { label: "İtiraza al", icon: "bi-chat-square-quote", btn: "inc-btn--dark", hint: "Onaylı → İtirazda. Bildirim artık sayılmaz ve diğer restoranlara görünmez." },
  restore: { label: "Geri al", icon: "bi-arrow-counterclockwise", btn: "inc-btn--success", hint: "İtirazda → Onaylı. Bildirim yeniden sayılır." },
  remove: { label: "Kaldır", icon: "bi-trash3", btn: "inc-btn--danger", hint: "Kalıcıdır: açıklama, müşteri eşleşmesi ve görsel silinir; geri alınamaz." },
  approve: { label: "Talebi onayla", icon: "bi-check-lg", btn: "inc-btn--success", hint: "Talep uygulanır (düzeltme ya da silme)." },
  reject: { label: "Talebi reddet", icon: "bi-x-lg", btn: "inc-btn--danger", hint: "Bildirim Onaylı durumuna döner ve yeniden sayılır." },
};

export default function IncidentDetailModal({ incidentId, restaurantName, onClose, onChanged }: Props) {
  const [data, setData] = useState<IIncidentHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [action, setAction] = useState<Action | null>(null);
  const [reason, setReason] = useState("");
  const [newType, setNewType] = useState<IncidentType | "">("");
  const [newDesc, setNewDesc] = useState("");
  const [saving, setSaving] = useState(false);

  const [evidence, setEvidence] = useState<{ url: string; loading: boolean; error?: string } | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    getAdminIncident(incidentId)
      .then((res) => setData(res.data))
      .catch((e) => setError(incidentError(e, "Bildirim yüklenemedi.")))
      .finally(() => setLoading(false));
  }, [incidentId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !saving) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  const inc = data?.incident;
  const openRequest: IChangeRequest | undefined = data?.changeRequests.find((r) => r.status === "Open");
  const live = inc && ["PendingConfirmation", "Confirmed", "Disputed"].includes(inc.status);

  const allowed: Record<Action, boolean> = {
    correct: !!live && !openRequest,
    dispute: inc?.status === "Confirmed" && !openRequest,
    restore: inc?.status === "Disputed" && !openRequest,
    remove: !!inc && inc.status !== "Removed" && !openRequest,
    approve: !!openRequest,
    reject: !!openRequest,
  };

  const startAction = (a: Action) => {
    setAction(a);
    setReason("");
    setError(null);
    if (a === "correct" && inc) { setNewType(inc.type); setNewDesc(inc.description); }
  };

  const trimmedReason = reason.trim();
  const correctionChanges = action === "correct" && inc
    ? { type: newType && newType !== inc.type ? newType : undefined, description: newDesc.trim() && newDesc.trim() !== inc.description ? newDesc.trim() : undefined }
    : null;
  const canSave = !!action && trimmedReason.length > 0 && trimmedReason.length <= REASON_MAX && !saving
    && (action !== "correct" || !!(correctionChanges?.type || correctionChanges?.description));

  const save = async () => {
    if (!action || !inc || !canSave) return;
    setSaving(true);
    setError(null);
    try {
      let res;
      switch (action) {
        case "correct": res = await adminCorrect(inc.id, { ...correctionChanges!, reason: trimmedReason }); break;
        case "dispute": res = await adminDispute(inc.id, trimmedReason); break;
        case "restore": res = await adminRestore(inc.id, trimmedReason); break;
        case "remove": res = await adminRemove(inc.id, trimmedReason); break;
        case "approve": res = await decideChangeRequest(openRequest!.id, true, trimmedReason); break;
        case "reject": res = await decideChangeRequest(openRequest!.id, false, trimmedReason); break;
      }
      setData(res.data);
      setAction(null);
      setEvidence(null);
      onChanged?.();
    } catch (e) {
      setError(incidentError(e, "İşlem yapılamadı."));
      // Kapanmış talep / eşzamanlı değişiklik olabilir — güncel hali göster
      load();
    } finally {
      setSaving(false);
    }
  };

  const showEvidence = () => {
    if (!inc) return;
    setEvidence({ url: "", loading: true });
    getEvidence(inc.id)
      .then((res) => setEvidence({ url: res.data.url, loading: false }))
      .catch((e) => setEvidence({ url: "", loading: false, error: incidentError(e, "Görsel alınamadı.") }));
  };

  const statusMeta = inc ? STATUS_META[inc.status] : null;

  return (
    <div className="inc-backdrop" onClick={() => !saving && onClose()}>
      <div className="inc-modal inc-modal--wide" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="inc-modal__head">
          <div className="inc-modal__icon" style={{ background: statusMeta?.bg ?? "#fef2f2", color: statusMeta?.color ?? "#dc2626" }}>
            <i className={`bi ${inc ? typeIcon(inc.type) : "bi-flag-fill"}`}></i>
          </div>
          <div style={{ minWidth: 0 }}>
            <div className="inc-modal__title">{inc ? typeLabel(inc.type) : "Olay bildirimi"}</div>
            <div className="inc-modal__sub">
              {restaurantName ?? "Restoran"}{inc?.orderCode ? ` · Sipariş #${inc.orderCode}` : ""}
            </div>
          </div>
          <button className="inc-close" onClick={onClose} disabled={saving} aria-label="Kapat"><i className="bi bi-x-lg"></i></button>
        </div>

        <div className="inc-modal__body">
          {loading && !data ? (
            <div style={{ textAlign: "center", padding: 30 }}><span className="spinner-border spinner-border-sm" style={{ color: "#dc2626" }} role="status" /></div>
          ) : !inc ? (
            error && <div className="inc-alert inc-alert--error"><i className="bi bi-exclamation-circle-fill"></i><div>{error}</div></div>
          ) : (
            <>
              {/* Özet */}
              <div className="inc-item" style={{ background: "#fafafa" }}>
                <div className="inc-item__top">
                  <StatusBadge status={inc.status} />
                  <span className="inc-badge" style={{ background: "#f1f5f9", color: "#475569" }}>
                    <i className="bi bi-person"></i>{REPORTER_LABEL[inc.createdByKind] ?? inc.createdByKind}
                  </span>
                  {inc.hasEvidence && (
                    <span className="inc-badge" style={{ background: "#eff6ff", color: "#1d4ed8" }}><i className="bi bi-image"></i>Görsel var</span>
                  )}
                </div>
                <div className="inc-item__desc" style={{ fontSize: ".86rem" }}>
                  {inc.description || <em style={{ color: "#9ca3af" }}>İçerik kaldırıldı</em>}
                </div>
                <div className="inc-item__meta">
                  <span><i className="bi bi-calendar-event me-1"></i>Olay: {formatDateTime(inc.occurredAtUtc)}</span>
                  <span><i className="bi bi-clock me-1"></i>Bildirim: {formatDateTime(inc.createdAtUtc)}</span>
                  {inc.reviewedAtUtc && <span><i className="bi bi-check2 me-1"></i>İnceleme: {formatDateTime(inc.reviewedAtUtc)}</span>}
                </div>
                <div className="inc-item__meta" style={{ fontFamily: "monospace" }}>
                  <span title="Bildiren kullanıcı">ID: {inc.createdByUserId}</span>
                </div>
              </div>

              {inc.hasEvidence && (
                <div>
                  {!evidence ? (
                    <button className="inc-btn inc-btn--sm" onClick={showEvidence}><i className="bi bi-image"></i> Görseli göster</button>
                  ) : evidence.loading ? (
                    <span className="spinner-border spinner-border-sm" style={{ color: "#dc2626" }} role="status" />
                  ) : evidence.error ? (
                    <div className="inc-alert inc-alert--error"><i className="bi bi-image"></i><div>{evidence.error}</div></div>
                  ) : (
                    <a href={evidence.url} target="_blank" rel="noopener noreferrer">
                      <img src={evidence.url} alt="Kanıt görseli" style={{ maxWidth: "100%", maxHeight: 320, borderRadius: 12, border: "1.5px solid #e5e7eb" }} />
                    </a>
                  )}
                </div>
              )}

              {/* Açık talep */}
              {openRequest && (
                <div className="inc-alert inc-alert--warn" style={{ flexDirection: "column", gap: 4 }}>
                  <div style={{ fontWeight: 800 }}>
                    <i className="bi bi-hourglass-split me-1"></i>
                    Restoranın açık talebi: {openRequest.kind === "Delete" ? "Silme" : "Düzeltme"}
                  </div>
                  <div><strong>Gerekçe:</strong> {openRequest.reason}</div>
                  {openRequest.kind === "Correct" && (
                    <>
                      {openRequest.proposedType && <div><strong>Önerilen tür:</strong> {typeLabel(openRequest.proposedType)}</div>}
                      {openRequest.proposedDescription && <div><strong>Önerilen açıklama:</strong> {openRequest.proposedDescription}</div>}
                    </>
                  )}
                  <div style={{ fontSize: ".72rem", opacity: .8 }}>{formatDateTime(openRequest.requestedAtUtc)}</div>
                </div>
              )}

              {/* İşlemler */}
              <div>
                <div className="inc-label">İşlemler</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {(openRequest ? (["approve", "reject"] as Action[]) : (["correct", "dispute", "restore", "remove"] as Action[]))
                    .filter((a) => allowed[a])
                    .map((a) => (
                      <button
                        key={a}
                        className={`inc-btn inc-btn--sm${action === a ? " inc-btn--dark" : a === "remove" ? " inc-btn--ghost-danger" : ""}`}
                        onClick={() => startAction(a)}
                        disabled={saving}
                      >
                        <i className={`bi ${ACTIONS[a].icon}`}></i>{ACTIONS[a].label}
                      </button>
                    ))}
                  {!openRequest && !Object.values(allowed).some(Boolean) && (
                    <span className="inc-hint" style={{ marginTop: 0 }}>Bu kayıt üzerinde yapılabilecek işlem yok.</span>
                  )}
                </div>
                {inc.status === "PendingConfirmation" && (
                  <div className="inc-hint">Onay/ret kararı restoran yöneticisine aittir; SuperAdmin düzeltebilir veya kaldırabilir.</div>
                )}
              </div>

              {action && (
                <div className="inc-card" style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
                  <div className="inc-alert inc-alert--info"><i className="bi bi-info-circle-fill"></i><div>{ACTIONS[action].hint}</div></div>
                  {action === "correct" && (
                    <>
                      <div>
                        <div className="inc-label">Tür</div>
                        <select className="inc-input" value={newType} onChange={(e) => setNewType(e.target.value as IncidentType)}>
                          {INCIDENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <div className="inc-label"><span>Açıklama</span><span style={{ color: "#9ca3af" }}>{newDesc.length}/{DESCRIPTION_MAX}</span></div>
                        <textarea className="inc-input" rows={3} maxLength={DESCRIPTION_MAX} value={newDesc} onChange={(e) => setNewDesc(e.target.value)} />
                      </div>
                    </>
                  )}
                  <div>
                    <div className="inc-label"><span>Gerekçe (geçmişe yazılır)</span><span style={{ color: "#9ca3af" }}>{reason.length}/{REASON_MAX}</span></div>
                    <textarea
                      className="inc-input"
                      rows={2}
                      maxLength={REASON_MAX}
                      placeholder="Ör. Sipariş kaydıyla uyumsuz, tür yanlış seçilmiş."
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      autoFocus={action !== "correct"}
                    />
                  </div>
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <button className="inc-btn inc-btn--sm" onClick={() => setAction(null)} disabled={saving}>Vazgeç</button>
                    <button className={`inc-btn inc-btn--sm ${ACTIONS[action].btn}`} onClick={save} disabled={!canSave}>
                      {saving ? <span className="spinner-border spinner-border-sm" role="status" /> : <><i className={`bi ${ACTIONS[action].icon}`}></i>{ACTIONS[action].label}</>}
                    </button>
                  </div>
                </div>
              )}

              {error && <div className="inc-alert inc-alert--error"><i className="bi bi-exclamation-circle-fill"></i><div>{error}</div></div>}

              {/* Geçmiş */}
              <div>
                <div className="inc-label">Geçmiş</div>
                <div className="inc-timeline">
                  {data!.revisions.map((r, idx) => (
                    <div key={idx} className="inc-tl">
                      <div className="inc-tl__dot" style={{ borderColor: r.actorKind === "SuperAdmin" ? "#7c3aed" : "#dc2626" }} />
                      <div className="inc-tl__body">
                        <div>
                          <strong>{ACTION_LABEL[r.action] ?? r.action}</strong>
                          <span style={{ color: "#6b7280" }}> · {ACTOR_LABEL[r.actorKind] ?? r.actorKind}</span>
                          {r.fromStatus && r.toStatus && r.fromStatus !== r.toStatus && (
                            <span style={{ color: "#6b7280" }}> · {STATUS_META[r.fromStatus]?.label} → {STATUS_META[r.toStatus]?.label}</span>
                          )}
                        </div>
                        {r.reason && <div style={{ marginTop: 2 }}>“{r.reason}”</div>}
                        {(r.previousType || r.previousDescription) && (
                          <div style={{ marginTop: 2, color: "#6b7280" }}>
                            Önceki: {r.previousType ? typeLabel(r.previousType) : ""}{r.previousType && r.previousDescription ? " — " : ""}{r.previousDescription ?? ""}
                          </div>
                        )}
                        <div style={{ color: "#9ca3af", fontSize: ".7rem", marginTop: 2 }}>{formatDateTime(r.atUtc)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Kapanmış talepler */}
              {data!.changeRequests.some((r) => r.status !== "Open") && (
                <div>
                  <div className="inc-label">Önceki talepler</div>
                  <div className="inc-list">
                    {data!.changeRequests.filter((r) => r.status !== "Open").map((r) => (
                      <div key={r.id} className="inc-item">
                        <div className="inc-item__top">
                          <span className="inc-item__title" style={{ fontSize: ".8rem" }}>{r.kind === "Delete" ? "Silme talebi" : "Düzeltme talebi"}</span>
                          <span className="inc-badge" style={{ background: "#f1f5f9", color: "#475569" }}>{REQUEST_STATUS_LABEL[r.status]}</span>
                        </div>
                        <div className="inc-item__desc">{r.reason}</div>
                        {r.decisionReason && <div className="inc-item__meta"><span>Karar: {r.decisionReason}</span></div>}
                        <div className="inc-item__meta"><span>{formatDateTime(r.requestedAtUtc)}</span>{r.decidedAtUtc && <span>→ {formatDateTime(r.decidedAtUtc)}</span>}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
